import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function GuidesSection() {
  const { t } = useTranslation();

  const cards = [
    {
      icon: "ri-file-list-3-line",
      key: "spreadsheet",
      tone: "primary",
      image:
        "https://readdy.ai/api/search-image?query=A%20tidy%20desk%20with%20an%20open%20laptop%20showing%20a%20colorful%20organized%20spreadsheet%20with%20several%20columns%2C%20a%20small%20cup%20of%20coffee%20beside%20it%2C%20bright%20soft%20daylight%2C%20clean%20minimal%20editorial%20photography%2C%20no%20text%2C%20no%20watermark&width=600&height=400&seq=guide-spreadsheet&orientation=landscape",
    },
    {
      icon: "ri-sparkling-2-line",
      key: "finds",
      tone: "accent",
      image:
        "https://readdy.ai/api/search-image?query=A%20curated%20flat%20lay%20of%20trendy%20streetwear%20products%20including%20sneakers%2C%20a%20cap%2C%20a%20watch%20and%20a%20small%20tote%20bag%20on%20a%20warm%20neutral%20background%2C%20soft%20natural%20light%2C%20fresh%20editorial%20product%20photography%2C%20no%20text%2C%20no%20watermark&width=600&height=400&seq=guide-finds&orientation=landscape",
    },
    {
      icon: "ri-truck-line",
      key: "shipping",
      tone: "secondary",
      image:
        "https://readdy.ai/api/search-image?query=A%20single%20brown%20cardboard%20parcel%20with%20a%20shipping%20label%20placed%20on%20a%20clean%20light%20desk%20next%20to%20a%20measuring%20tape%2C%20soft%20window%20light%2C%20minimal%20logistics%20concept%20photography%2C%20no%20text%2C%20no%20watermark%2C%20calm%20and%20neat&width=600&height=400&seq=guide-shipping&orientation=landscape",
    },
    {
      icon: "ri-search-eye-line",
      key: "qc",
      tone: "primary",
      image:
        "https://readdy.ai/api/search-image?query=A%20close-up%20of%20hands%20holding%20a%20magnifying%20glass%20over%20a%20folded%20t-shirt%20on%20a%20bright%20white%20table%2C%20clean%20quality%20inspection%20concept%2C%20soft%20even%20lighting%2C%20minimal%20product%20photography%2C%20no%20text%2C%20no%20watermark&width=600&height=400&seq=guide-qc&orientation=landscape",
    },
    {
      icon: "ri-star-line",
      key: "review",
      tone: "accent",
      image:
        "https://readdy.ai/api/search-image?query=A%20warm%20desk%20scene%20with%20a%20notebook%2C%20a%20pen%20and%20five%20small%20golden%20paper%20stars%20arranged%20in%20a%20row%2C%20soft%20morning%20light%2C%20minimal%20rating%20and%20review%20concept%20photography%2C%20no%20text%2C%20no%20watermark%2C%20cozy%20and%20bright&width=600&height=400&seq=guide-review&orientation=landscape",
    },
    {
      icon: "ri-shield-check-line",
      key: "safety",
      tone: "secondary",
      image:
        "https://readdy.ai/api/search-image?query=A%20clean%20minimal%20shield%20emblem%20made%20of%20soft%20mint%20green%20glass%20resting%20on%20a%20light%20wooden%20surface%20with%20soft%20daylight%2C%20calm%20security%20concept%20photography%2C%20no%20text%2C%20no%20watermark%2C%20gentle%20and%20reassuring&width=600&height=400&seq=guide-safety&orientation=landscape",
    },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-700",
    secondary: "bg-secondary-100 text-secondary-700",
  };

  return (
    <section id="guides" className="scroll-mt-24">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">
            {t("home.guides.title")}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            {t("home.guides.subtitle")}
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card) => (
            <div
              key={card.key}
              className="group overflow-hidden rounded-xl border border-background-200 bg-white transition-transform hover:-translate-y-1"
            >
              <div className="relative h-36 w-full overflow-hidden">
                <img
                  src={card.image}
                  alt={t(`home.guides.cards.${card.key}.title`)}
                  title={`${t(`home.guides.cards.${card.key}.title`)} — ${t("common.siteName")}`}
                  className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="flex items-start gap-4 p-5">
                <span
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${toneClass[card.tone]}`}
                >
                  <i className={`${card.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">
                    {t(`home.guides.cards.${card.key}.title`)}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">
                    {t(`home.guides.cards.${card.key}.desc`)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/guides/allchinabuy-spreadsheet"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 cursor-pointer"
          >
            {t("home.guides.cta")}
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}